/**
 * The v1 chat token usage popover.
 *
 * The popover body and the entitlement-derived data moved down into
 * `copilot-shared-ui` so the Copilot composers below this package can render
 * them; what stays here is the part that reads usage out of v1 chat state.
 */
import {useSafeChatState} from '@github-ui/copilot-chat-contexts/ChatStateContext'
import type {SessionTokenUsage} from '@github-ui/copilot-shared-ui/components/TokenUsageInfoContent'
import {TokenUsageInfoContent, useTokenUsageInfo} from '@github-ui/copilot-shared-ui/components/TokenUsageInfoContent'
import {calculateTurnCredits, normalizeTokenPrices} from '@github-ui/copilot-shared-ui/utils/model-pricing'
import {useMemo} from 'react'

function useSessionTokenUsage(): SessionTokenUsage | null {
  const chatState = useSafeChatState()
  const messages = chatState?.messages
  const availableModels = chatState?.availableModels

  return useMemo(() => {
    if (!messages) return null
    let inputTokens = 0
    let outputTokens = 0
    let totalCredits = 0
    let hasUsage = false
    let hasPricing = false
    const modelsById = availableModels ? new Map(availableModels.map(m => [m.id, m])) : undefined
    for (const msg of messages) {
      if (msg.role === 'assistant' && msg.usage) {
        inputTokens += msg.usage.inputTokens
        outputTokens += msg.usage.outputTokens
        hasUsage = true
        const model = msg.model ? modelsById?.get(msg.model) : undefined
        const pricing = normalizeTokenPrices(model?.billing?.token_prices)
        if (pricing) {
          totalCredits += calculateTurnCredits(msg.usage, pricing)
          hasPricing = true
        }
      }
    }
    return hasUsage
      ? {inputTokens, outputTokens, totalCredits: hasPricing ? Math.round(totalCredits * 100) / 100 : null}
      : null
  }, [messages, availableModels])
}

/**
 * Displays quota and session token usage information.
 * Quota data comes from the entitlement context. Session token usage is read from
 * the surrounding chat state when present; outside a `CopilotChatProvider` the
 * session usage section is simply omitted.
 */
export function TokenUsageInfo() {
  const data = useTokenUsageInfo()
  const sessionUsage = useSessionTokenUsage()

  return <TokenUsageInfoContent data={data} sessionUsage={sessionUsage} />
}
